import clamp from 'lodash/clamp'
import {
  GeckoComponentDataFrame,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import { FigureSelectionArea } from './regionTypes'

export interface SelectedBins {
  minCorner: [number, number]
  maxCorner: [number, number]
}

export function toBaseUnit(measurement: GeckoMeasurement): number {
  return measurement.convertTo(GeckoMeasurementUnit.Millimeters).num
}

export function estimatedBinCount(
  selection: FigureSelectionArea,
  binning: GeckoComponentDataFrame['data']['binning']
): number {
  const xLen = toBaseUnit(selection.x.end) - toBaseUnit(selection.x.start)
  const yLen = toBaseUnit(selection.y.end) - toBaseUnit(selection.y.start)

  const binsWide = Math.ceil(xLen / toBaseUnit(binning.bin_size.width))
  const binsHigh = Math.ceil(yLen / toBaseUnit(binning.bin_size.height))

  return binsWide * binsHigh
}

export function measurementToBin(
  x: GeckoMeasurement,
  y: GeckoMeasurement,
  binning: GeckoComponentDataFrame['data']['binning'],
  geometry: GeckoComponentDataFrame['data']['geometry']
): [number, number] {
  const binWidth = toBaseUnit(binning.bin_size.width)
  const binHeight = toBaseUnit(binning.bin_size.height)

  const binX = Math.floor(
    Number(
      ((toBaseUnit(x) - toBaseUnit(geometry.x.start)) / binWidth).toPrecision(8)
    )
  )
  const binY = Math.floor(
    Number(
      ((toBaseUnit(y) - toBaseUnit(geometry.y.start)) / binHeight).toPrecision(
        8
      )
    )
  )
  return [binX, binY]
}

export function selectionToBins(
  figureSelectionArea: FigureSelectionArea,
  binning: GeckoComponentDataFrame['data']['binning'],
  geometry: GeckoComponentDataFrame['data']['geometry']
): SelectedBins {
  const [rawMinX, rawMinY] = measurementToBin(
    figureSelectionArea.x.min,
    figureSelectionArea.y.min,
    binning,
    geometry
  )
  const [rawMaxX, rawMaxY] = measurementToBin(
    figureSelectionArea.x.max,
    figureSelectionArea.y.max,
    binning,
    geometry
  )

  // A region can be smaller than one bin, or fall partly outside the grid.
  // Clamp into the grid and guarantee it covers at least the one bin it
  // overlaps, so downstream half-open `[min, max)` indexing yields >=1 bin
  // instead of an empty/zero-width crop (which crashed inflateDataTo2D and
  // produced NaN damage-category percentages).
  const minX = clamp(rawMinX, 0, Math.max(binning.x_bin_count - 1, 0))
  const minY = clamp(rawMinY, 0, Math.max(binning.y_bin_count - 1, 0))
  const maxX = clamp(Math.max(rawMaxX, minX + 1), minX + 1, binning.x_bin_count)
  const maxY = clamp(Math.max(rawMaxY, minY + 1), minY + 1, binning.y_bin_count)

  return {
    minCorner: [minX, minY],
    maxCorner: [maxX, maxY],
  }
}

function clampBinRange(
  start: number,
  end: number,
  limit: number
): readonly [number, number] | null {
  const clampedStart = Math.max(0, Math.min(start, limit))
  const clampedEnd = Math.max(0, Math.min(end, limit))
  return clampedStart < clampedEnd ? [clampedStart, clampedEnd] : null
}

function splitBinRange(
  start: number,
  end: number,
  limit: number,
  wraps: boolean
): ReadonlyArray<readonly [number, number]> {
  const ranges =
    wraps && end < start
      ? [
          [start, limit],
          [0, end],
        ]
      : [[start, end]]
  return ranges
    .map(([rangeStart, rangeEnd]) => clampBinRange(rangeStart, rangeEnd, limit))
    .filter((range): range is readonly [number, number] => range !== null)
}

/** Converts a selection to canonical, half-open bin rectangles. */
export function selectionToCanonicalBins(
  selection: FigureSelectionArea,
  binning: GeckoComponentDataFrame['data']['binning'],
  geometry: GeckoComponentDataFrame['data']['geometry']
): ReadonlyArray<SelectedBins> {
  const [startX, startY] = measurementToBin(
    selection.x.start,
    selection.y.start,
    binning,
    geometry
  )
  const [endX, endY] = measurementToBin(
    selection.x.end,
    selection.y.end,
    binning,
    geometry
  )
  const wrapsX = endX < startX
  const wrapsY = endY < startY
  if (wrapsX && wrapsY) return []
  const xRanges = splitBinRange(startX, endX, binning.x_bin_count, wrapsX)
  const yRanges = splitBinRange(startY, endY, binning.y_bin_count, wrapsY)

  return xRanges.flatMap(([minX, maxX]) =>
    yRanges.map(([minY, maxY]) => ({
      minCorner: [minX, minY],
      maxCorner: [maxX, maxY],
    }))
  )
}

export function periodicSpan(
  start: number,
  end: number,
  period: number
): number {
  return end >= start ? end - start : end + period - start
}

export function periodicMidpoint(
  start: number,
  end: number,
  period: number
): number {
  return (start + periodicSpan(start, end, period) / 2) % period
}

export function extractBinSize(cdf: GeckoComponentDataFrame): [number, number] {
  return [
    cdf.data.binning.bin_size.width,
    cdf.data.binning.bin_size.height,
  ].map(
    (measurement) => measurement.convertTo(GeckoMeasurementUnit.Millimeters).num
  ) as [number, number]
}

export function binToMeasurement(
  binCoord: number,
  offset: number,
  binSize: number
) {
  return binCoord * binSize + offset + binSize / 2
}
